import { PersonScore } from "@/features/people/hooks/useDimensionsWithScores";
import { getIsValuesDimension } from "@/shared/utils/selectors/getDimensionTypes";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";

/**
 * Implementation based on: docs/PROFILES_DIMENSION_TEXTS_SELECTION_BL.md
 */

type Resource = "values" | "personality" | "competencies";
type ComparisonType = "individual" | "team";

interface AlignmentResult {
  /** i18n translation key for the alignment label (e.g. 'alignment_labels.aligned') */
  label: string;
  className: string;
  color: string;
  score: number | null;
}

interface ThresholdConfig {
  threshold: number;
  labelKey: string;
  className: string;
  color: string;
}

/**
 * Alignment configurations mapped by Context (Team/Individual) and Resource Type.
 * Logic defined in:
 * - Team Profile (Personality/Values): Based on team_stdev thresholds
 * - Comparison Profile (Individual): Based on score difference thresholds (<15, 15-25, >25)
 */
const ALIGNMENT_CONFIG: Record<
  ComparisonType,
  Record<string, ThresholdConfig[]>
> = {
  team: {
    // Labels for Value dimensions (stdev based)
    values: [
      {
        threshold: 10,
        labelKey: "highly_aligned",
        className: "bg-green-500 text-white",
        color: "green",
      },
      {
        threshold: 20,
        labelKey: "aligned",
        className: "bg-blue-500 text-white",
        color: "blue",
      },
      {
        threshold: 30,
        labelKey: "somewhat_diverse",
        className: "bg-orange-500 text-white",
        color: "orange",
      },
      {
        threshold: Infinity,
        labelKey: "very_diverse",
        className: "bg-red-500 text-white",
        color: "red",
      },
    ],
    // Labels for Personality dimensions (stdev based)
    personality: [
      {
        threshold: 10,
        labelKey: "very_similar",
        className: "bg-red-500 text-white",
        color: "red",
      },
      {
        threshold: 20,
        labelKey: "similar",
        className: "bg-orange-500 text-white",
        color: "orange",
      },
      {
        threshold: 30,
        labelKey: "somewhat_diverse",
        className: "bg-blue-500 text-white",
        color: "blue",
      },
      {
        threshold: Infinity,
        labelKey: "very_diverse",
        className: "bg-green-500 text-white",
        color: "green",
      },
    ],
  },
  individual: {
    personality: [
      {
        threshold: 10,
        labelKey: "highly_aligned",
        className: "bg-red-500 text-white",
        color: "red",
      },
      {
        threshold: 20,
        labelKey: "aligned",
        className: "bg-orange-500 text-white",
        color: "orange",
      },
      {
        threshold: 30,
        labelKey: "somewhat_different",
        className: "bg-blue-500 text-white",
        color: "blue",
      },
      {
        threshold: Infinity,
        labelKey: "very_diverse",
        className: "bg-green-500 text-white",
        color: "green",
      },
    ],
    // Default handles comparison context: (Diff < 15: Aligned | 15-25: Somewhat different | > 25: Different)
    default: [
      {
        threshold: 15,
        labelKey: "aligned",
        className: "bg-green-500 text-white",
        color: "green",
      },
      {
        threshold: 25,
        labelKey: "somewhat_different",
        className: "bg-orange-500 text-white",
        color: "orange",
      },
      {
        threshold: Infinity,
        labelKey: "different",
        className: "bg-red-500 text-white",
        color: "red",
      },
    ],
  },
};

export const getAlignment = (
  comparisonType: ComparisonType,
  compareScore: number,
  peopleScores: PersonScore[] | null,
  resource: Resource
): AlignmentResult => {
  let score: number | null = null;

  if (comparisonType === "individual" && peopleScores) {
    // Calculate Mean Absolute Difference for comparison context
    const validScores = peopleScores
      .map((s) => s.value)
      .filter((v): v is number => v !== null && v !== undefined);
    if (validScores.length === 0)
      return {
        label: "",
        className: "bg-gray-500 text-white",
        color: "grey",
        score: null,
      };

    const scoreDiffs = validScores.map((v) => Math.abs(v - compareScore));
    score = scoreDiffs.reduce((a, b) => a + b, 0) / validScores.length;
  } else if (comparisonType === "team" && resource !== "competencies") {
    // For teams, the comparison score is treated directly as the stdev
    score = compareScore;
  }

  if (score === null)
    return { label: "", className: "", color: "grey", score: null };

  const isValues = getIsValuesDimension(resource as AssessmentSurveyType);
  const resourceKey = isValues ? "values" : "personality";

  // Selection of config based on context and resource type
  const configs =
    ALIGNMENT_CONFIG[comparisonType][resourceKey] ||
    ALIGNMENT_CONFIG[comparisonType]["default"];
  const config =
    configs.find((c) => score! < c.threshold) || configs[configs.length - 1];

  return {
    label: `alignment_labels.${config.labelKey}`,
    className: config.className,
    color: config.color,
    score,
  };
};
